import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

// Shape of an individual class card entry is not yet confirmed against a
// real response sample — left loose until the actual fields are known.
export interface StaffClassDetail {
  [key: string]: any;
}

export interface SchoolAndStaffInfo {
  pkAcademicId: number;
  schoolName: string;
  SchoolDomain: string;
  schoolImage: string;
  currencyType: string;
  StaffId: number;
  schoolId: number;
  staffName: string;
  employmentId: string;
  mobileNumber: string;
  emailId: string;
  staffImage: string;
  dateOfJoining: string;
  dateOfBirth: string;
  address: string;
  gender: number;
  availability: string;
  countryCode: string;
  des_id: number;
  designation: string;
  isSuperAdmin: number;
  isEnabled: number;
  category: number;
  isClassTeacher: number;
  isDriver: number;
  isHostelStaff: number;
  isFinanceAdmin: number;
  currencySymbol: string;
  region: string;
  isFeeDefaulterAdmin: number;
  academicStartDate: string;
  academicEndDate: string;
  isUnreadNotification: number;
  [key: string]: any;
}

export interface GetClassDetailsResponse {
  status: boolean;
  body: {
    schoolAndStaffInfoData: SchoolAndStaffInfo[];
    schoolExtensionServicePermissions: any;
    maxPageNumber: number;
    features: any[];
    activityLikeTypes: any[];
    isAnyUnreadBroadcast: boolean;
    classDetails: StaffClassDetail[];
  };
}

export interface ParticularClassDetail {
  classId: number;
  subject: string | null;
  presentCount: number;
  lateCount: number;
  absentCount: number;
  leaveCount: number;
  attendanceType: string;
  attendancePercentage: number;
  studentCount: number;
  [key: string]: any;
}

export interface GetParticularClassInfoResponse {
  status: boolean;
  body: {
    classDetails: ParticularClassDetail[];
    isClassTeacher: boolean;
    isSuperAdmin: boolean;
  };
}

export interface StudentBasicInfo {
  rollNumber: number;
  studentId?: number;
  studentName?: string;
  studentImage?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class StaffClassService {
  private url = AppSettings.API_ENDPOINT_STAFF;
  // Some endpoints (student basic info, fee access) are not under /api/v3.
  private baseUrl = AppSettings.API_ENDPOINT_STAFF_BASE;
  private getClassDetailsEndpoint = apiConstants.getStaffDashboardClassDetails;
  private getParticularClassInfoEndpoint = apiConstants.getParticularClassInfo;
  private getStudentBasicInfoEndpoint = apiConstants.getStudentBasicInfo;
  private getStaffFeeAccessEndpoint = apiConstants.getStaffFeeAccess;

  constructor(private http: HttpClient) {}

  getClassDetails(staffId: number | string, pageNumber: number, search: string = '') {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('classSearchText', search)
      .set('pageNumber', pageNumber)
      .set('lang', 'en');

    return this.http
      .get<GetClassDetailsResponse>(`${this.url}${this.getClassDetailsEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  getParticularClassInfo(staffId: number | string, classId: number | string) {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('classId', classId)
      .set('lang', 'en');

    return this.http
      .get<GetParticularClassInfoResponse>(`${this.url}${this.getParticularClassInfoEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  // Backend only reads classId from the body — matches the mobile app's
  // own connector, which computes mobileNumber/schoolId/academicId but
  // never actually forwards them.
  getStudentBasicInfo(classId: number | string) {
    return this.http
      .post<StudentBasicInfo[]>(`${this.baseUrl}${this.getStudentBasicInfoEndpoint}`, { classId: String(classId) })
      .pipe(map((res) => res));
  }

  getStaffFeeAccess(staffId: number | string, classId: number | string) {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('classId', classId);

    return this.http
      .get<{ isStaffHasAccessForFees: boolean }[]>(`${this.baseUrl}${this.getStaffFeeAccessEndpoint}`, { params })
      .pipe(map((res) => res));
  }
}
